import { Suspense, lazy, type ReactNode } from 'react';
import { KpiRowSkeleton } from '@/components';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Skeleton } from 'antd';
import AppShell from '@/shell/AppShell';

/**
 * Route table.
 *
 * Sprint 1 update (S1-T05 / NFR-006):
 *  All routes are lazy-loaded to code-split the main bundle. Chart-heavy
 *  dashboards pull `@ant-design/plots` only after the user navigates to them,
 *  keeping the initial chunk small enough to hit the 800KB gzip target.
 *
 * Sprint mapping (for StubPage badges):
 *  - Sprint 1: shell + i18n/RTL + role switcher + mock data + dashboards + finance + hr
 *  - Sprint 2: engineering + operations + contracts + docs/drawings + 4 workflows
 *  - Sprint 3: marketing + legal + remaining 4 workflows + settings + audit + polish + deploy
 */

const Landing = lazy(() => import('@/pages/Landing'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const About = lazy(() => import('@/pages/About'));

// Module landings — replace the Navigate redirects on the module index routes
// with real dashboards (Theme 2d).
const FinanceLanding = lazy(() => import('@/pages/finance/FinanceLanding'));
const EngineeringLanding = lazy(() => import('@/pages/engineering/EngineeringLanding'));
const OperationsLanding = lazy(() => import('@/pages/operations/OperationsLanding'));
const ContractsLanding = lazy(() => import('@/pages/contracts/ContractsLanding'));
const HrLanding = lazy(() => import('@/pages/hr/HrLanding'));
const MarketingLanding = lazy(() => import('@/pages/marketing/MarketingLanding'));

// Finance — Sprint 1 Phase B
const FinanceCoa = lazy(() => import('@/pages/finance/ChartOfAccounts'));
const FinanceGl = lazy(() => import('@/pages/finance/GeneralLedger'));
const FinanceAp = lazy(() => import('@/pages/finance/AccountsPayable'));
const FinanceAr = lazy(() => import('@/pages/finance/AccountsReceivable'));
const FinanceBudget = lazy(() => import('@/pages/finance/Budget'));
const FinanceCostControl = lazy(() => import('@/pages/finance/CostControl'));
const FinanceCashFlow = lazy(() => import('@/pages/finance/CashFlow'));
const FinanceStatements = lazy(() => import('@/pages/finance/Statements'));
const FinanceVatCurrency = lazy(() => import('@/pages/finance/VatCurrency'));

// HR — Sprint 1 Phase B
const HREmployees = lazy(() => import('@/pages/hr/Employees'));
const HRAttendance = lazy(() => import('@/pages/hr/Attendance'));
const HRTimesheets = lazy(() => import('@/pages/hr/Timesheets'));
const HRLeave = lazy(() => import('@/pages/hr/Leave'));
const HRDocumentExpiry = lazy(() => import('@/pages/hr/DocumentExpiry'));
const HRPayroll = lazy(() => import('@/pages/hr/Payroll'));
const HRRecruitment = lazy(() => import('@/pages/hr/Recruitment'));
const HROrgChart = lazy(() => import('@/pages/hr/OrgChart'));

// Settings — Sprint 1 Phase B (reset + localization only; full suite in S3)
const Settings = lazy(() => import('@/pages/settings/Settings'));

// Engineering — Sprint 2
const EngProjects = lazy(() => import('@/pages/engineering/Projects'));
const EngBoq = lazy(() => import('@/pages/engineering/BOQ'));
const EngWbs = lazy(() => import('@/pages/engineering/WBS'));
const EngEstimates = lazy(() => import('@/pages/engineering/Estimates'));
const EngSubmittals = lazy(() => import('@/pages/engineering/Submittals'));
const EngDrawings = lazy(() => import('@/pages/engineering/Drawings'));
const EngBim = lazy(() => import('@/pages/engineering/BIM'));
const EngMaterialRequests = lazy(() => import('@/pages/engineering/MaterialRequests'));
const EngTechDocs = lazy(() => import('@/pages/engineering/TechDocs'));

// Contracts & Commercial — Sprint 2
const CtrClientContracts = lazy(() => import('@/pages/contracts/ClientContracts'));
const CtrSubcontracts = lazy(() => import('@/pages/contracts/Subcontracts'));
const CtrIpc = lazy(() => import('@/pages/contracts/IPC'));
const CtrVo = lazy(() => import('@/pages/contracts/VO'));
const CtrProfitability = lazy(() => import('@/pages/contracts/Profitability'));
const CtrRfq = lazy(() => import('@/pages/contracts/RFQ'));
const CtrRfp = lazy(() => import('@/pages/contracts/RFP'));
const CtrPo = lazy(() => import('@/pages/contracts/PO'));
const CtrClaims = lazy(() => import('@/pages/contracts/Claims'));
const CtrCorrespondence = lazy(() => import('@/pages/contracts/Correspondence'));
const CtrTenderPipeline = lazy(() => import('@/pages/contracts/TenderPipeline'));

// Operations — Sprint 2
const OpsSiteProgress = lazy(() => import('@/pages/operations/SiteProgress'));
const OpsResources = lazy(() => import('@/pages/operations/Resources'));
const OpsConsumption = lazy(() => import('@/pages/operations/Consumption'));
const OpsCloseOut = lazy(() => import('@/pages/operations/CloseOut'));
const OpsOmManuals = lazy(() => import('@/pages/operations/OmManuals'));
const OpsWarranty = lazy(() => import('@/pages/operations/Warranty'));
const OpsInventory = lazy(() => import('@/pages/operations/Inventory'));
const OpsAssets = lazy(() => import('@/pages/operations/Assets'));

// Marketing & BD — Sprint 3 Phase A
const MktLeads = lazy(() => import('@/pages/marketing/Leads'));
const MktClients = lazy(() => import('@/pages/marketing/Clients'));
const MktOpportunities = lazy(() => import('@/pages/marketing/Opportunities'));
const MktTenders = lazy(() => import('@/pages/marketing/Tenders'));
const MktActivities = lazy(() => import('@/pages/marketing/Activities'));
const MktReports = lazy(() => import('@/pages/marketing/Reports'));

// Legal — Sprint 3 Phase A
const LegalRedlining = lazy(() => import('@/pages/legal/Redlining'));
const LegalCompliance = lazy(() => import('@/pages/legal/Compliance'));
const LegalDisputes = lazy(() => import('@/pages/legal/Disputes'));
const LegalDashboard = lazy(() => import('@/pages/legal/Dashboard'));

// Workflows — Sprint 2 (catalog + four wizards)
const WorkflowCatalog = lazy(() => import('@/pages/workflows/Catalog'));
const WfContractBilling = lazy(() => import('@/pages/workflows/ContractBilling'));
const WfEstimateToCost = lazy(() => import('@/pages/workflows/EstimateToCost'));
const WfVoLifecycle = lazy(() => import('@/pages/workflows/VoLifecycle'));
const WfMaterialRequest = lazy(() => import('@/pages/workflows/MaterialRequest'));

// Workflows — Sprint 3 Phase B (four new wizards)
const WfAttendancePayroll = lazy(() => import('@/pages/workflows/AttendancePayroll'));
const WfClaimLifecycle = lazy(() => import('@/pages/workflows/ClaimLifecycle'));
const WfProgressDashboard = lazy(() => import('@/pages/workflows/ProgressDashboard'));
const WfLeadToAward = lazy(() => import('@/pages/workflows/LeadToAward'));

/**
 * Route-level skeleton — replaces the bare `<Spin />` with brand-shaped
 * skeleton blocks while a lazy route chunk is being fetched. Visually
 * communicates "the page is loading the same shape you'll see in a moment"
 * instead of a generic spinner. Matches the typical module-page layout
 * (header strip, KPI row, large content card).
 */
function Fallback(): ReactNode {
  return (
    <div className="ga-route-fallback" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Skeleton.Input active size="large" style={{ width: 240, height: 28 }} />
      <Skeleton paragraph={{ rows: 1 }} active title={false} />
      <KpiRowSkeleton count={4} />
      <Skeleton.Node active style={{ height: 280, width: '100%' }} />
    </div>
  );
}

function wrap(node: ReactNode): ReactNode {
  return <Suspense fallback={<Fallback />}>{node}</Suspense>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={wrap(<Landing />)} />
        <Route path="dashboard" element={wrap(<Dashboard />)} />

        {/* Finance — Sprint 1 */}
        <Route path="finance">
          <Route index element={wrap(<FinanceLanding />)} />
          <Route path="coa" element={wrap(<FinanceCoa />)} />
          <Route path="chart-of-accounts" element={wrap(<Navigate to="/finance/coa" replace />)} />
          <Route path="gl" element={wrap(<FinanceGl />)} />
          <Route path="ap" element={wrap(<FinanceAp />)} />
          <Route path="ar" element={wrap(<FinanceAr />)} />
          <Route path="budget" element={wrap(<FinanceBudget />)} />
          <Route path="budgets" element={wrap(<Navigate to="/finance/budget" replace />)} />
          <Route path="cost-control" element={wrap(<FinanceCostControl />)} />
          <Route path="cash-flow" element={wrap(<FinanceCashFlow />)} />
          <Route path="statements" element={wrap(<FinanceStatements />)} />
          <Route path="vat-currency" element={wrap(<FinanceVatCurrency />)} />
          <Route path="vat" element={wrap(<Navigate to="/finance/vat-currency" replace />)} />
          <Route path="currency" element={wrap(<Navigate to="/finance/vat-currency" replace />)} />
        </Route>

        {/* Engineering — Sprint 2 */}
        <Route path="engineering">
          <Route index element={wrap(<EngineeringLanding />)} />
          <Route path="projects" element={wrap(<EngProjects />)} />
          <Route path="boq" element={wrap(<EngBoq />)} />
          <Route path="wbs" element={wrap(<EngWbs />)} />
          <Route path="estimates" element={wrap(<EngEstimates />)} />
          <Route path="submittals" element={wrap(<EngSubmittals />)} />
          <Route path="drawings" element={wrap(<EngDrawings />)} />
          <Route path="bim" element={wrap(<EngBim />)} />
          <Route path="material-requests" element={wrap(<EngMaterialRequests />)} />
          <Route path="tech-docs" element={wrap(<EngTechDocs />)} />
          {/* NOTE: /engineering/progress removed — S-curve lives inside Project detail → Progress tab (FR-049). */}
        </Route>

        {/* Operations — Sprint 2 */}
        <Route path="operations">
          <Route index element={wrap(<OperationsLanding />)} />
          <Route path="site-progress" element={wrap(<OpsSiteProgress />)} />
          <Route path="resources" element={wrap(<OpsResources />)} />
          <Route path="consumption" element={wrap(<OpsConsumption />)} />
          <Route path="close-out" element={wrap(<OpsCloseOut />)} />
          <Route path="om-manuals" element={wrap(<OpsOmManuals />)} />
          <Route path="warranty" element={wrap(<OpsWarranty />)} />
          <Route path="inventory" element={wrap(<OpsInventory />)} />
          <Route path="assets" element={wrap(<OpsAssets />)} />
        </Route>

        {/* Contracts & Commercial — Sprint 2 */}
        <Route path="contracts">
          <Route index element={wrap(<ContractsLanding />)} />
          <Route path="clients" element={wrap(<CtrClientContracts />)} />
          <Route path="client" element={wrap(<Navigate to="/contracts/clients" replace />)} />
          <Route path="subcontracts" element={wrap(<CtrSubcontracts />)} />
          <Route path="sub" element={wrap(<Navigate to="/contracts/subcontracts" replace />)} />
          <Route path="ipc" element={wrap(<CtrIpc />)} />
          <Route path="variation-orders" element={wrap(<CtrVo />)} />
          <Route path="vo" element={wrap(<Navigate to="/contracts/variation-orders" replace />)} />
          <Route path="profitability" element={wrap(<CtrProfitability />)} />
          <Route path="rfq" element={wrap(<CtrRfq />)} />
          <Route path="rfp" element={wrap(<CtrRfp />)} />
          <Route path="po" element={wrap(<CtrPo />)} />
          <Route path="claims" element={wrap(<CtrClaims />)} />
          <Route path="correspondence" element={wrap(<CtrCorrespondence />)} />
          <Route path="tender-pipeline" element={wrap(<CtrTenderPipeline />)} />
        </Route>

        {/* HR — Sprint 1 */}
        <Route path="hr">
          <Route index element={wrap(<HrLanding />)} />
          <Route path="employees" element={wrap(<HREmployees />)} />
          <Route path="attendance" element={wrap(<HRAttendance />)} />
          <Route path="timesheets" element={wrap(<HRTimesheets />)} />
          <Route path="leave" element={wrap(<HRLeave />)} />
          <Route path="document-expiry" element={wrap(<HRDocumentExpiry />)} />
          <Route path="documents" element={wrap(<Navigate to="/hr/document-expiry" replace />)} />
          <Route path="payroll" element={wrap(<HRPayroll />)} />
          <Route path="payroll/:periodId" element={wrap(<HRPayroll />)} />
          <Route path="recruitment" element={wrap(<HRRecruitment />)} />
          <Route path="org-chart" element={wrap(<HROrgChart />)} />
        </Route>

        {/* Marketing & BD — Sprint 3 Phase A */}
        <Route path="marketing">
          <Route index element={wrap(<MarketingLanding />)} />
          <Route path="leads" element={wrap(<MktLeads />)} />
          <Route path="clients" element={wrap(<MktClients />)} />
          <Route path="opportunities" element={wrap(<MktOpportunities />)} />
          <Route path="tenders" element={wrap(<MktTenders />)} />
          <Route path="activities" element={wrap(<MktActivities />)} />
          <Route path="reports" element={wrap(<MktReports />)} />
          {/* Legal queue previously sat under /marketing/legal; S3 moves it to its
              own /legal tree. Redirect preserves old links. */}
          <Route path="legal" element={wrap(<Navigate to="/legal/redlining" replace />)} />
        </Route>

        {/* Legal — Sprint 3 Phase A */}
        <Route path="legal">
          <Route index element={wrap(<Navigate to="/legal/redlining" replace />)} />
          <Route path="redlining" element={wrap(<LegalRedlining />)} />
          <Route path="compliance" element={wrap(<LegalCompliance />)} />
          <Route path="disputes" element={wrap(<LegalDisputes />)} />
          <Route path="dashboard" element={wrap(<LegalDashboard />)} />
        </Route>

        {/* Workflows — Sprint 2 + Sprint 3 */}
        <Route path="workflows">
          <Route index element={wrap(<WorkflowCatalog />)} />
          <Route path="contract-billing" element={wrap(<WfContractBilling />)} />
          <Route path="estimate-cost" element={wrap(<WfEstimateToCost />)} />
          <Route path="vo-lifecycle" element={wrap(<WfVoLifecycle />)} />
          <Route path="material-request" element={wrap(<WfMaterialRequest />)} />
          {/* Sprint 3 Phase B */}
          <Route path="attendance-payroll" element={wrap(<WfAttendancePayroll />)} />
          <Route path="claim-lifecycle" element={wrap(<WfClaimLifecycle />)} />
          <Route path="progress-dashboard" element={wrap(<WfProgressDashboard />)} />
          <Route path="lead-to-award" element={wrap(<WfLeadToAward />)} />
        </Route>
        <Route path="settings">
          <Route index element={wrap(<Navigate to="/settings/data" replace />)} />
          {/*
            Settings tabs are rendered by a single component which reads the
            active tab from a `:tab` URL param (useParams().tab). Using a
            dynamic segment lets the Tabs onChange handler navigate across
            /settings/data, /settings/localization, /settings/integrations,
            /settings/audit without defining four near-identical static routes
            (which broke useParams — QA P1, 2026-04-24).
          */}
          <Route path=":tab" element={wrap(<Settings />)} />
        </Route>
        {/* Legacy /audit URL — audit trail now lives under Settings/Audit. */}
        <Route path="audit" element={wrap(<Navigate to="/settings/audit" replace />)} />
        <Route path="about" element={wrap(<About />)} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
